import React, { useEffect, useMemo, useState } from 'react';
import activoClassificationService from '../../services/activoClassificationService';
import { getAreas } from '../../services/areaService';
import { DOCUMENT_TYPE_OPTIONS } from '../../utils/documentTypes';

const emptyForm = {
  code: '',
  name: '',
  brand: '',
  model: '',
  description: '',
  category_id: '',
  subcategory_id: '',
  activo_family_id: '',
  area_id: '',
  serial_number: '',
  notes: '',
};

const DocumentActivoModal = ({
  isOpen,
  onClose,
  onSave,
  documentContext,
  initialActivo = null,
  suggestedCode = '',
}) => {
  const [formData, setFormData] = useState(emptyForm);
  const [hierarchy, setHierarchy] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const docTypeLabel =
    DOCUMENT_TYPE_OPTIONS.find((o) => o.value === documentContext?.document_type)?.label ||
    documentContext?.document_type ||
    '—';

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      activoClassificationService.getHierarchy({ active_only: true }),
      getAreas({ active_only: true }),
    ])
      .then(([hierarchyRes, areasRes]) => {
        if (cancelled) return;
        if (hierarchyRes.success) setHierarchy(hierarchyRes.data || []);
        if (areasRes.success) setAreas(areasRes.data || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setFormError('');
    if (initialActivo) {
      const family = initialActivo.family;
      const sub = family?.subcategory;
      const cat = sub?.category;
      setFormData({
        code: initialActivo.code || '',
        name: initialActivo.name || '',
        brand: initialActivo.brand || '',
        model: initialActivo.model || '',
        description: initialActivo.description || '',
        category_id: cat?.id ? String(cat.id) : initialActivo.category_id || '',
        subcategory_id: sub?.id ? String(sub.id) : initialActivo.subcategory_id || '',
        activo_family_id: initialActivo.activo_family_id ? String(initialActivo.activo_family_id) : '',
        area_id: initialActivo.area_id ? String(initialActivo.area_id) : '',
        serial_number: initialActivo.serial_number || '',
        notes: initialActivo.notes || '',
      });
    } else {
      setFormData({
        ...emptyForm,
        code: suggestedCode || '',
      });
    }
  }, [isOpen, initialActivo, suggestedCode]);

  const subcategories = useMemo(() => {
    if (!formData.category_id) return [];
    const cat = hierarchy.find((c) => String(c.id) === String(formData.category_id));
    return cat?.subcategories || [];
  }, [hierarchy, formData.category_id]);

  const families = useMemo(() => {
    if (!formData.subcategory_id) return [];
    const sub = subcategories.find((s) => String(s.id) === String(formData.subcategory_id));
    return sub?.families || [];
  }, [subcategories, formData.subcategory_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'category_id') {
        next.subcategory_id = '';
        next.activo_family_id = '';
      }
      if (name === 'subcategory_id') {
        next.activo_family_id = '';
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.activo_family_id) {
      setFormError('Seleccione categoría, subcategoría y familia del activo.');
      return;
    }
    if (!formData.area_id) {
      setFormError('Seleccione el área de empresa donde ubicará el activo.');
      return;
    }
    if (!formData.brand.trim()) {
      setFormError('Ingrese la marca del activo.');
      return;
    }
    if (!formData.model.trim()) {
      setFormError('Ingrese el modelo del activo.');
      return;
    }
    if (hierarchy.length === 0) {
      setFormError('No hay clasificación de activos configurada. Configure categorías en Activos → Configuración.');
      return;
    }
    if (areas.length === 0) {
      setFormError('No hay áreas de empresa configuradas. Configure áreas en Activos → Configuración → Áreas.');
      return;
    }
    setFormError('');
    setSaving(true);
    const family = families.find((f) => String(f.id) === String(formData.activo_family_id));
    const area = areas.find((a) => String(a.id) === String(formData.area_id));
    onSave({
      id: initialActivo?.id || null,
      code: formData.code.trim(),
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      activo_family_id: parseInt(formData.activo_family_id, 10),
      area_id: parseInt(formData.area_id, 10),
      brand: formData.brand.trim(),
      model: formData.model.trim(),
      serial_number: formData.serial_number.trim() || null,
      status: 'pending_start',
      notes: formData.notes.trim() || null,
      is_active: true,
      acquisition_quantity: initialActivo?.acquisition_quantity ?? 1,
      acquisition_unit_price: initialActivo?.acquisition_unit_price ?? null,
      family,
      area,
    });
    setSaving(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {initialActivo ? 'Editar activo pendiente' : 'Preparar activo desde factura'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Clasificación e identificación del activo. Cantidad y costo se completan en el formulario principal.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="rounded-lg border border-teal-100 bg-teal-50/50 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Proveedor:</span>{' '}
              <span className="font-medium text-gray-900">{documentContext?.supplier_name || '—'}</span>
            </div>
            <div>
              <span className="text-gray-500">Documento:</span>{' '}
              <span className="font-medium text-gray-900">
                {docTypeLabel} {documentContext?.series}-{documentContext?.number}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Fecha emisión:</span>{' '}
              <span className="font-medium text-gray-900">{documentContext?.issue_date || '—'}</span>
            </div>
            <div>
              <span className="text-gray-500">Moneda:</span>{' '}
              <span className="font-medium text-gray-900">{documentContext?.currency || 'PEN'}</span>
            </div>
          </div>

          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          {loading ? (
            <p className="text-gray-600">Cargando clasificación y áreas…</p>
          ) : hierarchy.length === 0 ? (
            <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm">
              No hay clasificación de activos. Configure categorías, subcategorías y familias en{' '}
              <strong>Activos → Configuración → Clasificación</strong>.
            </p>
          ) : areas.length === 0 ? (
            <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm">
              No hay áreas de empresa. Configure áreas en <strong>Activos → Configuración → Áreas</strong>.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                  <input
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca *</label>
                  <input
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modelo *</label>
                  <input
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">N° serie</label>
                  <input
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoría *</label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Seleccione…</option>
                    {hierarchy.map((c) => (
                      <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subcategoría *</label>
                  <select
                    name="subcategory_id"
                    value={formData.subcategory_id}
                    onChange={handleChange}
                    required
                    disabled={!formData.category_id}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-100"
                  >
                    <option value="">Seleccione…</option>
                    {subcategories.map((s) => (
                      <option key={s.id} value={s.id}>{s.code} — {s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Familia *</label>
                  <select
                    name="activo_family_id"
                    value={formData.activo_family_id}
                    onChange={handleChange}
                    required
                    disabled={!formData.subcategory_id}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-100"
                  >
                    <option value="">Seleccione…</option>
                    {families.map((f) => (
                      <option key={f.id} value={f.id}>{f.code} — {f.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Área de empresa *</label>
                  <select
                    name="area_id"
                    value={formData.area_id}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Seleccione…</option>
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                  <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <p className="mr-auto text-xs text-gray-500 self-center hidden sm:block">
              El activo no se crea aún; quedará en la lista hasta que guarde el documento.
            </p>
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || saving || hierarchy.length === 0 || areas.length === 0}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {initialActivo ? 'Actualizar en lista' : 'Añadir a la lista'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentActivoModal;
